import React, { useMemo, useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { isColorOptionsField, isLockedStatusOption, isPrioritiesField, isStatusField } from '../../data/formFieldPresets';
import './fieldOptionsPanel.css';

function OptionDeleteCell({ option, onDelete, locked = false }) {
  return (
    <td>
      {locked ? null : (
        <button
          className="field-options-panel__delete"
          type="button"
          aria-label={`Delete ${option}`}
          title={`Delete ${option}`}
          onClick={() => onDelete(option)}
        >
          <Trash2 size={14} strokeWidth={1.5} color="currentColor" aria-hidden="true" />
        </button>
      )}
    </td>
  );
}

function normalizeHex(value) {
  const hex = String(value || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase();
  }
  return '#000000';
}

export default function FieldOptionsPanel({ formFields = [], selectedField = null, onChange }) {
  const activeField = selectedField;
  const colorField = isColorOptionsField(activeField) ? activeField : null;
  const parentField = useMemo(() => {
    const dependsOn = String(activeField?.dependsOn || '').trim();
    if (!dependsOn) return null;
    return formFields.find((field) => String(field.fieldLabel || '').trim() === dependsOn) || null;
  }, [activeField?.dependsOn, formFields]);
  const parentOptions = parentField?.options || [];
  const showParentMapping = Boolean(activeField?.dependsOn);
  const [open, setOpen] = useState(false);

  const updateOptionColor = (option, color) => {
    if (!colorField) return;
    const hex = /^#[0-9A-Fa-f]{6}$/.test(color) ? color.toUpperCase() : color;
    onChange?.(formFields.map((field) => (
      field.id === colorField.id
        ? { ...field, optionColors: { ...field.optionColors, [option]: hex } }
        : field
    )));
  };

  const updateParentMapping = (option, parent) => {
    if (!activeField) return;
    onChange?.(formFields.map((field) => (
      field.id === activeField.id
        ? {
          ...field,
          parentMappings: {
            ...(field.parentMappings || {}),
            [option]: parent,
          },
        }
        : field
    )));
  };

  const deleteFieldOption = (optionToDelete) => {
    if (!activeField) return;
    if (isStatusField(activeField) && isLockedStatusOption(optionToDelete)) return;
    const nextColors = { ...(activeField.optionColors || {}) };
    delete nextColors[optionToDelete];
    const nextMappings = { ...(activeField.parentMappings || {}) };
    delete nextMappings[optionToDelete];
    onChange?.(formFields.map((field) => (
      field.id === activeField.id
        ? {
          ...field,
          options: (field.options || []).filter((option) => option !== optionToDelete),
          parentMappings: nextMappings,
          ...(isColorOptionsField(field) ? { optionColors: nextColors } : {}),
        }
        : field
    )));
  };

  const titleLabel = activeField?.fieldLabel || 'Select field';
  const description = !activeField
    ? ''
    : showParentMapping
      ? 'Define options and set parent.'
      : isStatusField(activeField)
        ? 'Manage the status'
        : isPrioritiesField(activeField)
          ? 'Manage your fields.'
          : 'Manage your fields.';

  const optionList = activeField?.options || [];
  const optionTable = (headers, rows) => (
    <div className="field-options-panel__table-wrap">
      <table className="field-options-panel__table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th scope="col" key={header}>{header}</th>
            ))}
            <th scope="col" aria-label="Delete" />
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );

  let body = (
    <p className="field-options-panel__empty">
      {activeField ? 'No options added.' : 'Select a field to edit.'}
    </p>
  );

  if (showParentMapping) {
    body = optionList.length ? optionTable(
      ['Option label', parentField?.fieldLabel || 'Parent'],
      optionList.map((option) => (
        <tr key={option}>
          <td>{option}</td>
          <td>
            <select
              aria-label={`Parent for ${option}`}
              value={activeField.parentMappings?.[option] || ''}
              disabled={!parentOptions.length}
              onChange={(event) => updateParentMapping(option, event.target.value)}
            >
              <option value="">
                {parentOptions.length ? 'Select parent' : `No ${String(activeField.dependsOn || 'parent').toLowerCase()} options`}
              </option>
              {parentOptions.map((parentOption) => (
                <option key={parentOption} value={parentOption}>
                  {parentOption}
                </option>
              ))}
            </select>
          </td>
          <OptionDeleteCell
            option={option}
            onDelete={deleteFieldOption}
            locked={isStatusField(activeField) && isLockedStatusOption(option)}
          />
        </tr>
      )),
    ) : (
      <p className="field-options-panel__empty">No options added.</p>
    );
  } else if (colorField) {
    body = optionList.length ? optionTable(
      [isStatusField(colorField) ? 'Status' : 'Option', 'Colors'],
      optionList.map((option) => {
        const hex = normalizeHex(colorField.optionColors?.[option]);
        return (
          <tr key={option}>
            <td>{option}</td>
            <td>
              <div className="field-options-panel__color">
                <input
                  className="field-options-panel__hex"
                  aria-label={`Color for ${option}`}
                  value={colorField.optionColors?.[option] || hex}
                  onChange={(event) => updateOptionColor(option, event.target.value)}
                />
                <label className="field-options-panel__swatch" style={{ background: hex }} title={hex}>
                  <input
                    type="color"
                    aria-label={`Pick color for ${option}`}
                    value={hex}
                    onChange={(event) => updateOptionColor(option, event.target.value)}
                  />
                </label>
              </div>
            </td>
            <OptionDeleteCell
              option={option}
              onDelete={deleteFieldOption}
              locked={isStatusField(colorField) && isLockedStatusOption(option)}
            />
          </tr>
        );
      }),
    ) : (
      <p className="field-options-panel__empty">No options added.</p>
    );
  } else if (optionList.length) {
    body = optionTable(
      [isPrioritiesField(activeField) ? 'Priorities' : 'Option'],
      optionList.map((option) => (
        <tr key={option}>
          <td>{option}</td>
          <OptionDeleteCell
            option={option}
            onDelete={deleteFieldOption}
            locked={isStatusField(activeField) && isLockedStatusOption(option)}
          />
        </tr>
      )),
    );
  }

  return (
    <aside
      className={`field-options-panel${open ? ' is-open' : ''}${activeField ? '' : ' field-options-panel--empty'}`}
      aria-label={`Options for: ${titleLabel}`}
    >
      <button
        type="button"
        className="field-options-panel__summary"
        aria-expanded={open}
        aria-controls="field-options-panel-body"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="field-options-panel__summary-text">
          <span className="field-options-panel__title">
            Options for: {titleLabel}
          </span>
          {description ? (
            <span className="field-options-panel__description">{description}</span>
          ) : null}
        </span>
        <ChevronDown className="field-options-panel__chevron" size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>

      <div id="field-options-panel-body" className="field-options-panel__body">
        {body}
      </div>
    </aside>
  );
}
